import { useMemo } from 'react';
import { useField, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import type { ReportingPerson } from '@gokrew/models/reporting';
import { FieldWrapper } from '@gokrew/ui/form/FieldWrapper';
import { ManagerPicker } from '@/components/reporting/ManagerPicker';
import type { AddEmployeeFormValues } from '@/types/employee';

/**
 * "Reports to" on the Add employee form: the new hire's own manager, set
 * right after the save (otherwise they start with no manager and their leave
 * goes to HR). Someone picked to report to the new hire is not offered.
 */
export const ReportsToField = () => {
  const { t } = useTranslation(TranslationNS.Employee);
  const [field, meta, helpers] = useField<ReportingPerson | null>('reportsTo');
  const { values } = useFormikContext<AddEmployeeFormValues>();

  const excludeIds = useMemo(() => {
    const selection = values.managesTeam ? values.teamSelection : undefined;
    if (!selection) return [];
    return [
      ...selection.people.map((person) => person.employeeId),
      ...(selection.replaced && selection.replacedJoins
        ? [selection.replaced.employeeId]
        : []),
    ];
  }, [values.managesTeam, values.teamSelection]);

  return (
    <FieldWrapper
      name="reportsTo"
      label={t('form.fields.reports_to.label')}
      infoText={t('form.fields.reports_to.info')}
      errorMeta={meta}
    >
      <ManagerPicker
        value={field.value ?? null}
        onChange={(person) => helpers.setValue(person)}
        excludeIds={excludeIds}
        placeholder={t('form.fields.reports_to.placeholder')}
      />
    </FieldWrapper>
  );
};
